import { useReadQuery } from '@apollo/client';
import { BadgeV2 } from '@synoptic/ui-kit/badge-v2/badge-v2.js';
import { ButtonV2 } from '@synoptic/ui-kit/button-v2/button-v2.js';
import { FollowersV2 } from '@synoptic/ui-kit/followers-v2/followers-v2.js';
import { HandleV2 } from '@synoptic/ui-kit/handle-v2/handle-v2.js';
import { ArrowLeftIcon } from '@synoptic/ui-kit/icons/react/arrow-left.js';
import { KeyIcon } from '@synoptic/ui-kit/icons/react/key.tsx';
import { LockSlashIcon } from '@synoptic/ui-kit/icons/react/lock-slash.tsx';
import { MoreHorizIcon } from '@synoptic/ui-kit/icons/react/more-horiz.js';
import { ShareIcon } from '@synoptic/ui-kit/icons/react/share.js';
import {
  PriceTagV2,
  type PriceTagV2Price,
} from '@synoptic/ui-kit/price-tag-v2/price-tag-v2.js';
import { useIsLaptop } from '@synoptic/ui-kit/responsive/hooks.js';
import { useToast } from '@synoptic/ui-kit/toast/toast-provider.tsx';
import { clsx } from 'clsx';
import { type ReactNode, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  StreamQueryQuery,
  StreamState,
  StreamType,
} from '@/__generated__/graphql.ts';
import { Stream, StreamDeck } from '@/__generated__/schema-types.ts';
import { UnauthenticatedActionDrawer } from '@/components/auth-guard/unauthenticated-action-drawer.tsx';
import { UnauthenticatedActionModal } from '@/components/auth-guard/unauthenticated-action-modal.tsx';
import { useNavigateBack } from '@/components/page-header/use-navigate-back.ts';
import { getUserAvatarSrc } from '@/components/s3/user/get-user-avatar-src.ts';
import {
  streamCardPriceV2,
  streamPageSubscriberPriceV2,
} from '@/components/stream-card-v2/stream-card-price-v2.ts';
import {
  StreamDeckColumnModal,
  type StreamDeckColumnSelection,
} from '@/components/stream-deck/stream-deck-column-modal.tsx';
import { BannerActionsMenu } from '@/components/streams/stream-banner/banner-actions-menu.tsx';
import { BannerDescription } from '@/components/streams/stream-banner/banner-description.tsx';
import { hasRightsToStream } from '@/components/streams/stream-banner/has-rights-to-stream';
import { SubscriptionExpiryNotice } from '@/components/streams/stream-banner/subscription-expiry-notice.tsx';
import { StreamSoundButtonV2 } from '@/components/streams/toggle-notif-sound/stream-sound-button.tsx';
import { ChoosePackageModal } from '@/components/streams/toggle-paid-stream-subscription/stream-included-in-package-modal.tsx';
import { SubscribePaidStreamButton } from '@/components/streams/toggle-paid-stream-subscription/subscribe-paid-stream-button.tsx';
import { Link } from '@/routes-utils/navigation.tsx';
import { paths } from '@/routes-utils/paths.ts';
import { CustomTargetsData } from '@/routes/_app.p.streams_.$id/stream-banner/custom-stream-components/custom-targets-data.tsx';
import { MeStreamActions } from '@/routes/_app.p.streams_.$id/stream-banner/me-stream-actions.tsx';
import {
  setStreamUsersType,
  StreamUserType,
} from '@/routes/_app.p.streams_.$id_.users/stream-users-type.ts';
import { useAuthInfo } from '@/routes/_app/use-auth-info';
import { useMe } from '@/routes/_app/use-me.ts';
import { useStreamDecks } from '@/routes/_app/use-stream-decks.ts';
import { convertCentsToDollars } from '@/utils/convert-cents-to-dollars.ts';
import { copyUrlToClipboard } from '@/utils/copy-url-to-clipboard.ts';
import { formatNumber } from '@/utils/format-number.ts';
import { getMonthlyPrice } from '@/utils/paid-data.ts';
import { CustomStreamExplainerV2 } from './custom-stream-explainer-v2.tsx';
import { CustomStreamTargetsV2 } from './custom-stream-targets-v2.tsx';
import {
  AuthenticatedStreamLoaderData,
  UnauthenticatedStreamLoaderData,
  useStreamLoaderData,
} from './loader.ts';
import { MonitorPaidStreamAccess } from './monitor-paid-stream-access.tsx';
import {
  streamPageActionsV2,
  streamPageApiAccessV2,
  streamPageBackV2,
  streamPageDescriptionTextV2,
  streamPageDescriptionV2,
  streamPageFollowersButtonV2,
  streamPageFollowersV2,
  streamPageHeaderIdentityV2,
  streamPageHeaderMainV2,
  streamPageHeaderTopV2,
  streamPageHeaderV2,
  streamPageHeaderWashV2,
  streamPageHostV2,
  streamPageLiveV2,
  streamPageNoteV2,
  streamPageTagV2,
  streamPageTagsV2,
  streamPageTitleV2,
} from './stream-page-v2.css.ts';
import {
  useFollowStream,
  useUnfollowStream,
} from './use-toggle-follow-stream.ts';

const ShareStreamButton = ({ streamId }: { streamId: string }) => {
  const toast = useToast();
  return (
    <ButtonV2
      type="button"
      variant="secondary"
      endIcon={<ShareIcon />}
      onClick={() => {
        copyUrlToClipboard(paths.makeStream(streamId));
        toast.info({ title: 'Link copied to clipboard' });
      }}
    >
      Share
    </ButtonV2>
  );
};

const stateLabels: Record<StreamState, string | null> = {
  [StreamState.Active]: null,
  [StreamState.Draft]: 'Draft',
  [StreamState.Inactive]: 'Inactive',
  [StreamState.OnHold]: 'On hold',
  [StreamState.Deleted]: 'Deleted',
};

const visibilityLabels: Record<StreamType, string> = {
  [StreamType.Public]: 'Public',
  [StreamType.Private]: 'Private',
};

type HeaderStream = {
  name: string;
  description?: string | null;
  descriptionJson?: string | null;
  label?: string | null;
  followersCount: number;
  author: { id: string; username: string; avatar?: { key: string } | null };
  customTargetsData?: object | null;
};

type HeaderCatalogue = Pick<
  NonNullable<StreamQueryQuery['stream']>,
  | 'streamState'
  | 'streamType'
  | 'packageOnly'
  | 'paidData'
  | 'customTargetsData'
  | 'apiAccessEnabled'
>;

const Tag = ({
  children,
  className,
  title,
}: {
  children: ReactNode;
  className?: string;
  title?: string;
}) => (
  <BadgeV2
    className={clsx(streamPageTagV2, className)}
    tone="tag"
    title={title}
  >
    {children}
  </BadgeV2>
);

const StreamFollowersCount = ({
  count,
  onOpen,
}: {
  count: string;
  onOpen?: () => void;
}) => {
  if (!onOpen) {
    return <FollowersV2 className={streamPageFollowersV2} count={count} />;
  }

  return (
    <button
      type="button"
      className={clsx(streamPageFollowersV2, streamPageFollowersButtonV2)}
      onClick={onOpen}
      aria-label={`${count} subscribers`}
      data-testid="stream-subscribers"
    >
      <FollowersV2 count={count} />
    </button>
  );
};

const StreamPageHeaderFrame = ({
  stream,
  catalogue,
  actions,
  customNote,
  onOpenSubscribers,
  subscriptionExpiresAt,
  price,
}: {
  stream: HeaderStream;
  catalogue?: HeaderCatalogue;
  actions: ReactNode;
  customNote?: ReactNode;
  onOpenSubscribers?: () => void;
  subscriptionExpiresAt?: string;
  price?: PriceTagV2Price;
}) => {
  const { description, descriptionJson, author } = stream;
  const kind = stream.customTargetsData ? 'custom' : 'data';
  const stateLabel = catalogue && stateLabels[catalogue.streamState];
  const monthlyPrice = catalogue
    ? getMonthlyPrice(catalogue.paidData)
    : undefined;
  const targetFacts =
    catalogue?.customTargetsData && monthlyPrice != null
      ? {
          data: catalogue.customTargetsData,
          price: convertCentsToDollars(monthlyPrice),
        }
      : null;
  const navigateBack = useNavigateBack(
    kind === 'custom' ? paths.customStreams : paths.streams,
  );

  return (
    <>
      <button type="button" className={streamPageBackV2} onClick={navigateBack}>
        <ButtonV2
          variant="secondary"
          size="icon"
          aria-hidden
          tabIndex={-1}
          nativeButton={false}
          render={<span />}
        >
          <ArrowLeftIcon width={16} height={16} />
        </ButtonV2>
        {kind === 'custom' ? 'Custom stream' : 'Data stream'}
      </button>
      <section
        className={clsx(streamPageHeaderV2, streamPageHeaderWashV2[kind])}
      >
        <div className={streamPageHeaderTopV2}>
          <div className={streamPageHeaderIdentityV2}>
            {catalogue?.streamState === StreamState.Active ? (
              <span className={streamPageLiveV2}>LIVE</span>
            ) : null}
            {stateLabel ? <Tag>{stateLabel}</Tag> : null}
            {catalogue ? (
              <Tag>{visibilityLabels[catalogue.streamType]}</Tag>
            ) : null}
            {catalogue ? (
              <Tag
                className={streamPageApiAccessV2}
                title={
                  catalogue.apiAccessEnabled
                    ? 'External API consumers can subscribe to this stream'
                    : 'External API consumers cannot subscribe to this stream'
                }
              >
                {catalogue.apiAccessEnabled ? (
                  <KeyIcon width={14} height={14} aria-hidden />
                ) : (
                  <LockSlashIcon width={14} height={14} aria-hidden />
                )}
                {catalogue.apiAccessEnabled
                  ? 'API access'
                  : 'API access disabled'}
              </Tag>
            ) : null}
            <span className={streamPageHostV2}>
              Hosted by
              <HandleV2
                handle={author.username}
                avatarSrc={getUserAvatarSrc(
                  author.id,
                  author.avatar?.key,
                  'v2s',
                )}
                render={<Link to={paths.makeUser(author.username)} />}
              />
            </span>
          </div>
          <div className={streamPageActionsV2}>{actions}</div>
        </div>
        <div className={streamPageHeaderMainV2}>
          <h1 className={streamPageTitleV2}>{stream.name}</h1>
          {description || descriptionJson ? (
            <div className={streamPageDescriptionV2}>
              {descriptionJson ? (
                <BannerDescription descriptionJson={descriptionJson} />
              ) : (
                <p className={streamPageDescriptionTextV2}>{description}</p>
              )}
            </div>
          ) : null}
          {kind === 'custom'
            ? (customNote ?? <CustomStreamExplainerV2 />)
            : null}
          {targetFacts ? (
            <div className={streamPageNoteV2}>
              <CustomTargetsData
                customTargetsData={targetFacts.data}
                price={targetFacts.price}
              />
            </div>
          ) : null}
          {subscriptionExpiresAt ? (
            <div className={streamPageNoteV2}>
              <SubscriptionExpiryNotice cancelAt={subscriptionExpiresAt} />
            </div>
          ) : null}
          <div className={streamPageTagsV2}>
            {stream.label ? <Tag>{stream.label}</Tag> : null}
            {catalogue ? (
              <PriceTagV2 price={price ?? streamCardPriceV2(catalogue)} />
            ) : null}
            <StreamFollowersCount
              count={formatNumber(stream.followersCount)}
              onOpen={onOpenSubscribers}
            />
          </div>
        </div>
      </section>
    </>
  );
};

const FollowStreamButton = ({
  stream,
  toggleFollow,
  followLoading,
}: {
  stream: NonNullable<StreamQueryQuery['stream']>;
  toggleFollow: () => void;
  followLoading: boolean;
}) => {
  const hasRights = hasRightsToStream(stream);
  const isDraft = stream.streamState === StreamState.Draft;
  const paid = Boolean(stream.paidData?.length);
  const [searchParams] = useSearchParams();
  const paymentSuccess = searchParams.get('success') === 'true';

  if (paid && !hasRights) {
    return (
      <SubscribePaidStreamButton
        streamId={stream.id}
        loading={paymentSuccess}
        packages={stream.inPackages}
        prices={stream.paidData ?? []}
        customTargets={stream.customTargetsData ?? undefined}
      />
    );
  }

  if (stream.packageOnly && !hasRights) {
    return null;
  }

  if (stream.inPackages?.length && !stream.following && !hasRights) {
    return (
      <ChoosePackageModal
        trigger={
          <ButtonV2
            type="button"
            variant="primary"
            disabled={isDraft || followLoading}
            data-testid="follow-stream-button"
          >
            Follow
          </ButtonV2>
        }
        packages={stream.inPackages}
        title="Follow stream"
        actionTitle="Follow stream only"
        onSubmit={toggleFollow}
      />
    );
  }

  return (
    <ButtonV2
      type="button"
      variant="primary"
      disabled={(isDraft && !stream.following) || followLoading}
      onClick={toggleFollow}
      data-testid="follow-stream-button"
    >
      {stream.following ? 'Following' : 'Follow'}
    </ButtonV2>
  );
};

const AuthenticatedStreamPageHeader = () => {
  const { streamQueryRef } =
    useStreamLoaderData() as AuthenticatedStreamLoaderData;
  const { data } = useReadQuery(streamQueryRef);
  const stream = data?.stream;
  const me = useMe();
  const { isAdmin } = useAuthInfo();
  const navigate = useNavigate();
  const streamDecks = useStreamDecks() as StreamDeck[];
  const [followStream, followResult] = useFollowStream(stream?.id ?? '');
  const [unfollowStream, unfollowResult] = useUnfollowStream(stream?.id ?? '');
  const [selection, setSelection] = useState<StreamDeckColumnSelection | null>(
    null,
  );
  const [soundSettingsOpen, setSoundSettingsOpen] = useState(false);

  if (!stream) {
    return null;
  }

  const isMe = me.username === stream.author.username;
  const hasRights = hasRightsToStream(stream);
  const canOpenSubscribers =
    stream.streamType === StreamType.Private &&
    stream.streamState !== StreamState.Draft &&
    (isMe || isAdmin);
  const targets = stream.customTargets ?? [];
  const showInlineTargets = !isMe && targets.length > 0;
  const toggleFollow = stream.following ? unfollowStream : followStream;
  const showSound =
    stream.accessAllowed &&
    (!stream.customTargetsData || stream.customTargets?.length);

  return (
    <>
      <StreamPageHeaderFrame
        stream={stream}
        catalogue={stream}
        customNote={
          showInlineTargets ? (
            <CustomStreamTargetsV2
              stream={stream}
              targets={targets}
              canEdit={hasRights}
            />
          ) : undefined
        }
        onOpenSubscribers={
          canOpenSubscribers
            ? () => {
                setStreamUsersType(
                  stream.id,
                  stream.paidData?.length
                    ? StreamUserType.Subscribers
                    : StreamUserType.Followers,
                );
                navigate(paths.makeStreamUsers(stream.id));
              }
            : undefined
        }
        subscriptionExpiresAt={
          !isMe &&
          stream.paidData?.length &&
          stream.subscription?.active &&
          stream.subscription.cancelAt
            ? stream.subscription.cancelAt
            : undefined
        }
        price={
          !isMe && stream.subscription?.active
            ? streamPageSubscriberPriceV2(
                stream.subscription,
                stream.customTargetsData,
              )
            : undefined
        }
        actions={
          <>
            <BannerActionsMenu
              trigger={
                <ButtonV2
                  type="button"
                  variant="secondary"
                  size="icon"
                  aria-label="Stream actions"
                >
                  <MoreHorizIcon />
                </ButtonV2>
              }
              streamId={stream.id}
              isAuthor={isMe}
              canAddToStreamDeck={stream.canAddToStreamDeck}
              isPaidStream={Boolean(stream.paidData?.length)}
              isCancelled={Boolean(stream.subscription?.cancelAt)}
              accessAllowed={hasRights}
              streamDecks={streamDecks}
              onStreamColumnSelect={(streamDeck, column) =>
                setSelection({ streamDeck, column })
              }
              isCustomStream={Boolean(stream.customTargetsData)}
              isPrivateStream={stream.streamType === StreamType.Private}
              isDraft={stream.streamState === StreamState.Draft}
              onOpenSoundSettings={
                showSound ? () => setSoundSettingsOpen(true) : undefined
              }
            />
            {showSound ? (
              <StreamSoundButtonV2
                streamId={stream.id}
                settingsOpen={soundSettingsOpen}
                onSettingsOpenChange={setSoundSettingsOpen}
              />
            ) : null}
            <ShareStreamButton streamId={stream.id} />
            {isMe ? (
              <MeStreamActions stream={stream} />
            ) : (
              <FollowStreamButton
                stream={stream}
                toggleFollow={toggleFollow}
                followLoading={followResult.loading || unfollowResult.loading}
              />
            )}
          </>
        }
      />
      {selection ? (
        <StreamDeckColumnModal
          stream={stream as unknown as Stream}
          selection={selection}
          onClose={() => setSelection(null)}
        />
      ) : null}
      {!isMe && stream.paidData?.length ? (
        <MonitorPaidStreamAccess
          streamId={stream.id}
          accessAllowed={stream.accessAllowed}
        />
      ) : null}
    </>
  );
};

const GuestStreamPageHeader = () => {
  const { streamQueryRef } =
    useStreamLoaderData() as UnauthenticatedStreamLoaderData;
  const { data } = useReadQuery(streamQueryRef);
  const stream = data?.stream;
  const isLaptop = useIsLaptop();
  const [authOpen, setAuthOpen] = useState(false);

  if (!stream) {
    return null;
  }

  return (
    <>
      <StreamPageHeaderFrame
        stream={stream}
        actions={
          <>
            <ShareStreamButton streamId={stream.id} />
            <ButtonV2
              type="button"
              variant="primary"
              onClick={() => setAuthOpen(true)}
              data-testid="follow-stream-button"
            >
              Follow
            </ButtonV2>
          </>
        }
      />
      {isLaptop ? (
        <UnauthenticatedActionModal
          open={authOpen}
          onOpenChange={setAuthOpen}
        />
      ) : (
        <UnauthenticatedActionDrawer
          open={authOpen}
          onOpenChange={setAuthOpen}
        />
      )}
    </>
  );
};

export const StreamPageHeaderV2 = () => {
  const { authenticated } = useStreamLoaderData();
  return authenticated ? (
    <AuthenticatedStreamPageHeader />
  ) : (
    <GuestStreamPageHeader />
  );
};
